import { configureStore } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query/react";
import { dealsApi } from "@/slices/deals/dealsApi";
import dealsUiReducer from "@/slices/deals/dealsUi";
import dealsWizardReducer from "@/slices/deals/dealsWizard";

import { datesApi } from "@/slices/dates/datesApi";

import { propertiesApi, publicPropertiesApi } from "@/slices/properties/propertiesApi";
import propertiesUiReducer from "@/slices/properties/propertiesUi";
import propertiesWizardReducer from "@/slices/properties/propertiesWizard";
import { personsApi } from "@/slices/persons/personsApi";
import personsUiReducer from "@/slices/persons/personsUi";
import { eventsApi } from "@/slices/events/eventsApi";
import eventsUiReducer from "@/slices/events/eventsUi";
import { usersApi, publicUsersApi } from "@/slices/users/usersApi";
import { insightsApi } from "@/slices/insights/insightsApi";
import insightsUiReducer from "@/slices/insights/insightsUi";

import { invitesApi, publicInvitesApi } from "@/slices/invites/invitesApi";
import invitesUiReducer from "@/slices/invites/invitesUi";

import { onboardingApi } from "@/slices/onboarding/onboardingApi";
import onboardingUiReducer from "@/slices/onboarding/onboardingUi";

import { workflowTemplatesApi } from "./slices/workflows/workflowTemplatesApi";
import { workflowsApi } from "./slices/workflows/workflowsApi";
import { stepTemplatesApi } from "./slices/workflows/stepTemplatesApi";
import { eventTemplatesApi } from "./slices/eventTemplates/eventTemplatesApi";
import { stepsApi } from "./slices/steps/stepsApi";
import workflowsUI from "./slices/workflows/workflowsUi";
import eventTemplatesWizardReducer from "@/slices/eventTemplates/eventTemplatesWizard";

import { documentsApi } from "./slices/documents/documentsApi";
import { documentTemplatesApi } from "./slices/documentTemplates/documentTemplatesApi";

import { notificationsApi } from "./slices/notifications/notificationsApi";

import { teamApi } from "./slices/team/teamApi";
import { teamsApi } from "./slices/team/teamsApi";
import teamUi from "./slices/team/teamUi";

import { companyApi } from "./slices/company/companyApi";
import companyUi from "./slices/company/companyUi";

import { libraryApi } from "./slices/library/libraryApi";

import personalSettingsReducer from "./slices/settings/personalSettings";
import teamSettingsReducer from "./slices/settings/teamSettings";
import companySettingsReducer from "./slices/settings/companySettings";

import { signaturesApi } from "./slices/signatures/signaturesApi";

import authUiReducer from "./slices/auth/authUi";
import layoutUiReducer from "./slices/layout/layoutUi";

import { mapsApi } from "@/slices/maps/mapsApi";
import { assetsApi } from "@/slices/assets/assetsApi";
import { remindersApi } from "./slices/reminders/remindersApi";
import { ocrApi } from "./slices/ocr/ocrApi";
import { documentTemplateBatchApi } from "./slices/documentTemplateBatch/documentTemplateBatchApi";
import { clientNeedsApi } from "./slices/clientNeeds/clientNeedsApi";
import { listingSheetsApi } from "./slices/listingSheets/listingSheetsApi";
import { listingsApi } from "./slices/listings/listingsApi";
import { matchingApi } from "./slices/matching/matchingApi";
import { newsfeedApi } from "./slices/newsfeed/newsfeedApi";
import { homeApi } from "./slices/home/homeApi";
import { activitiesApi } from "./slices/activities/activitiesApi";
import { activityApi } from "./slices/activity/activityApi";
import { calendarApi } from "./slices/calendar/calendarApi";
import { clausesApi } from "./slices/clauses/clausesApi";
import { brokerageApi } from "./slices/brokerage/brokerageApi";
import { fieldLearningApi } from "./slices/fieldLearning/fieldLearningApi";
import { inboxApi } from "./slices/inbox/inboxApi";
import { foldersApi } from "./slices/folders/foldersApi";
import {
  listsApi,
  publicListsApi,
  favoritesApi,
} from "./slices/lists/listsApi";
import { projectPropertiesApi } from "./slices/projectProperties/projectPropertiesApi";
import { partiesApi } from "./slices/parties/partiesApi";
import { searchApi } from "./slices/search/searchApi";
import { conciergeApi } from "./slices/concierge/conciergeApi";

// listing/push pillar (REP-205 — supply-side WORK IT + REPORT IT + PUSH)
import { listingInterestApi } from "./slices/listingInterest/listingInterestApi";
import { listingActivityApi } from "./slices/listingActivity/listingActivityApi";
import { listingReportApi } from "./slices/listingReport/listingReportApi";
import { pushApi } from "./slices/push/pushApi";
import { blastApi } from "./slices/blast/blastApi";
import { contentApi } from "./slices/content/contentApi";
import { flowApi } from "./slices/flow/flowApi";
import { availabilityApi } from "./slices/availability/availabilityApi";

export const store = configureStore({
  reducer: {
    // deals
    [dealsApi.reducerPath]: dealsApi.reducer,
    dealsUi: dealsUiReducer,
    dealsWizard: dealsWizardReducer,

    // dates
    [datesApi.reducerPath]: datesApi.reducer,

    // properties
    [propertiesApi.reducerPath]: propertiesApi.reducer,
    [publicPropertiesApi.reducerPath]: publicPropertiesApi.reducer,
    propertiesUi: propertiesUiReducer,
    propertiesWizard: propertiesWizardReducer,

    // persons
    [personsApi.reducerPath]: personsApi.reducer,
    personsUi: personsUiReducer,

    // events
    [eventsApi.reducerPath]: eventsApi.reducer,
    eventsUi: eventsUiReducer,

    // users
    [usersApi.reducerPath]: usersApi.reducer,
    [publicUsersApi.reducerPath]: publicUsersApi.reducer,

    // insights
    [insightsApi.reducerPath]: insightsApi.reducer,
    insightsUi: insightsUiReducer,

    // invites
    [invitesApi.reducerPath]: invitesApi.reducer,
    [publicInvitesApi.reducerPath]: publicInvitesApi.reducer,
    invitesUi: invitesUiReducer,

    // workflows
    [workflowTemplatesApi.reducerPath]: workflowTemplatesApi.reducer,
    [workflowsApi.reducerPath]: workflowsApi.reducer,
    [stepTemplatesApi.reducerPath]: stepTemplatesApi.reducer,
    [eventTemplatesApi.reducerPath]: eventTemplatesApi.reducer,
    [stepsApi.reducerPath]: stepsApi.reducer,
    workflowsUi: workflowsUI,
    eventTemplatesWizard: eventTemplatesWizardReducer,

    // onboarding
    [onboardingApi.reducerPath]: onboardingApi.reducer,
    onboardingUi: onboardingUiReducer,

    // documents
    [documentsApi.reducerPath]: documentsApi.reducer,

    // document templates
    [documentTemplatesApi.reducerPath]: documentTemplatesApi.reducer,

    // notifications
    [notificationsApi.reducerPath]: notificationsApi.reducer,

    // team
    [teamApi.reducerPath]: teamApi.reducer,
    [teamsApi.reducerPath]: teamsApi.reducer,
    teamUi: teamUi,

    // company
    [companyApi.reducerPath]: companyApi.reducer,
    companyUi: companyUi,

    // library
    [libraryApi.reducerPath]: libraryApi.reducer,

    // settings
    personalSettings: personalSettingsReducer,
    teamSettings: teamSettingsReducer,
    companySettings: companySettingsReducer,

    // signatures
    [signaturesApi.reducerPath]: signaturesApi.reducer,

    // auth
    authUi: authUiReducer,
    layoutUi: layoutUiReducer,

    // maps
    [mapsApi.reducerPath]: mapsApi.reducer,

    // assets
    [assetsApi.reducerPath]: assetsApi.reducer,

    // reminders
    [remindersApi.reducerPath]: remindersApi.reducer,

    // ocr
    [ocrApi.reducerPath]: ocrApi.reducer,

    // document template batches
    [documentTemplateBatchApi.reducerPath]: documentTemplateBatchApi.reducer,

    // client needs
    [clientNeedsApi.reducerPath]: clientNeedsApi.reducer,

    // listing sheets (seller rep agreements)
    [listingSheetsApi.reducerPath]: listingSheetsApi.reducer,

    // standalone listings (REP-211 — create without a rep, link-rep)
    [listingsApi.reducerPath]: listingsApi.reducer,

    // matching (ClientNeed ↔ ListingSheet by postal-code radius)
    [matchingApi.reducerPath]: matchingApi.reducer,

    // newsfeed
    [newsfeedApi.reducerPath]: newsfeedApi.reducer,

    // home (Dashboard redesign — composed aggregators: needs-you queue)
    [homeApi.reducerPath]: homeApi.reducer,

    // activities
    [activitiesApi.reducerPath]: activitiesApi.reducer,

    // activity (REP-199 — parent-deal aggregated feed, cursor-paginated)
    [activityApi.reducerPath]: activityApi.reducer,

    // calendar
    [calendarApi.reducerPath]: calendarApi.reducer,

    // clauses
    [clausesApi.reducerPath]: clausesApi.reducer,

    // brokerage
    [brokerageApi.reducerPath]: brokerageApi.reducer,

    // field learning
    [fieldLearningApi.reducerPath]: fieldLearningApi.reducer,

    // inbox
    [inboxApi.reducerPath]: inboxApi.reducer,

    // folders
    [foldersApi.reducerPath]: foldersApi.reducer,

    // lists (REP-200 — polymorphic lists, favorites, saved searches)
    [listsApi.reducerPath]: listsApi.reducer,
    [publicListsApi.reducerPath]: publicListsApi.reducer,
    [favoritesApi.reducerPath]: favoritesApi.reducer,

    // project properties (REP-199 Phase 3 — pursuit stage transitions)
    [projectPropertiesApi.reducerPath]: projectPropertiesApi.reducer,

    // parties (deal-page Parties tab — sides aggregation)
    [partiesApi.reducerPath]: partiesApi.reducer,

    // global search (command palette — People · Deals · Properties)
    [searchApi.reducerPath]: searchApi.reducer,

    // concierge (REP-211 — AI assistant /assistant; confirm round-trip)
    [conciergeApi.reducerPath]: conciergeApi.reducer,

    // listing/push pillar (REP-205 — inbound work-it, owner report, push composer)
    [listingInterestApi.reducerPath]: listingInterestApi.reducer,
    [listingActivityApi.reducerPath]: listingActivityApi.reducer,
    [listingReportApi.reducerPath]: listingReportApi.reducer,
    [pushApi.reducerPath]: pushApi.reducer,

    // listing/push pillar (REP-205 Phase 2 — marketing blasts + AI content)
    [blastApi.reducerPath]: blastApi.reducer,
    [contentApi.reducerPath]: contentApi.reducer,
    [flowApi.reducerPath]: flowApi.reducer,
    [availabilityApi.reducerPath]: availabilityApi.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(
      dealsApi.middleware,
      datesApi.middleware,
      propertiesApi.middleware,
      publicPropertiesApi.middleware,
      personsApi.middleware,
      eventsApi.middleware,
      usersApi.middleware,
      publicUsersApi.middleware,
      insightsApi.middleware,
      invitesApi.middleware,
      publicInvitesApi.middleware,
      onboardingApi.middleware,
      workflowTemplatesApi.middleware,
      workflowsApi.middleware,
      stepTemplatesApi.middleware,
      eventTemplatesApi.middleware,
      stepsApi.middleware,
      documentsApi.middleware,
      documentTemplatesApi.middleware,
      notificationsApi.middleware,
      teamApi.middleware,
      teamsApi.middleware,
      companyApi.middleware,
      libraryApi.middleware,
      mapsApi.middleware,
      signaturesApi.middleware,
      assetsApi.middleware,
      remindersApi.middleware,
      ocrApi.middleware,
      documentTemplateBatchApi.middleware,
      clientNeedsApi.middleware,
      listingSheetsApi.middleware,
      listingsApi.middleware,
      matchingApi.middleware,
      newsfeedApi.middleware,
      homeApi.middleware,
      activitiesApi.middleware,
      activityApi.middleware,
      calendarApi.middleware,
      clausesApi.middleware,
      brokerageApi.middleware,
      fieldLearningApi.middleware,
      inboxApi.middleware,
      foldersApi.middleware,
      listsApi.middleware,
      publicListsApi.middleware,
      favoritesApi.middleware,
      projectPropertiesApi.middleware,
      partiesApi.middleware,
      searchApi.middleware,
      conciergeApi.middleware,
      listingInterestApi.middleware,
      listingActivityApi.middleware,
      listingReportApi.middleware,
      pushApi.middleware,
      blastApi.middleware,
      contentApi.middleware,
      flowApi.middleware,
      availabilityApi.middleware
    ),
});

setupListeners(store.dispatch);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
